import React from 'react';
import { Link } from 'react-router-dom';

/**
 * AI IMAGE PROMPT FOR study-plan-main.jpg:
 * 
 * "Modern flat design illustration of an AI-powered personalized study planner dashboard, 
 * showing a weekly calendar with colorful subject blocks (Physics-blue, Chemistry-green, 
 * Math-purple, Biology-orange), progress bars, time slots, and adaptive scheduling. 
 * Include floating holographic AI brain icon suggesting intelligent planning, clock icons, 
 * checkmarks for completed tasks, and notification badges. Clean interface with gradient 
 * background (purple to blue), modern UI elements, soft shadows, minimalist style, 
 * professional educational design, 4K quality, vibrant colors."
 * 
 * Alternative prompt:
 * "3D isometric illustration of a smart study planning interface, showing an AI assistant 
 * organizing study materials, calendar with color-coded subjects, progress tracking charts, 
 * goal setting dashboard, and adaptive schedule recommendations. Modern tech aesthetic with 
 * purple and blue gradients, floating UI elements, clean design, professional educational 
 * platform illustration, high quality render, 8K."
 */

interface StudyPlanFeature {
  icon: string;
  title: string;
  description: string;
  color: string;
}

const StudyPlanSection: React.FC = () => {
  const features: StudyPlanFeature[] = [
    {
      icon: 'fas fa-brain',
      title: 'AI-Powered Planning',
      description: 'Intelligent algorithm analyzes your strengths, weaknesses, and exam date to create optimal study schedules.',
      color: '#667eea'
    },
    {
      icon: 'fas fa-sync-alt',
      title: 'Adaptive Scheduling',
      description: 'Plan automatically adjusts based on your progress, learning speed, and performance in mock tests.',
      color: '#2d9cdb'
    },
    {
      icon: 'fas fa-clock',
      title: 'Time Optimization',
      description: 'Considers your sleep patterns, coaching classes, and commitments to maximize study efficiency.',
      color: '#f39c12'
    },
    {
      icon: 'fas fa-chart-line',
      title: 'Progress Tracking',
      description: 'Visual analytics show daily, weekly, and monthly progress with chapter-wise completion status.',
      color: '#27ae60'
    },
    {
      icon: 'fas fa-tasks',
      title: 'Priority-Based Tasks',
      description: 'High-priority topics and weak areas get more focus while maintaining balanced subject coverage.',
      color: '#e74c3c'
    },
    {
      icon: 'fas fa-mobile-alt',
      title: 'On-the-Go Access',
      description: 'Access your personalized study plan anytime on mobile, tablet, or desktop with sync across devices.',
      color: '#9b59b6'
    }
  ];

  const planSteps = [
    {
      step: '1',
      title: 'Select Exam & Level',
      description: 'Choose JEE/NEET and current preparation stage',
      icon: 'fas fa-graduation-cap'
    },
    {
      step: '2',
      title: 'Set Goals & Scores',
      description: 'Define target scores for each subject',
      icon: 'fas fa-bullseye'
    },
    {
      step: '3',
      title: 'Assess Strengths',
      description: 'Identify strong and weak chapters',
      icon: 'fas fa-star'
    },
    {
      step: '4',
      title: 'Add Schedule',
      description: 'Input study patterns and commitments',
      icon: 'fas fa-calendar-alt'
    },
    {
      step: '5',
      title: 'Get Your Plan',
      description: 'AI generates personalized study roadmap',
      icon: 'fas fa-rocket'
    }
  ];

  return (
    <div className="study-plan-section py-5" 
      style={{ 
        background: 'linear-gradient(to bottom, #ffffff 0%, #f8f9ff 100%)'
      }}>
      <div className="container py-5">
        
        {/* Section Header */}
        <div className="text-center mb-5 pb-4">
          <div className="d-inline-flex align-items-center px-4 py-2 mb-3 rounded-pill"
            style={{
              background: 'linear-gradient(135deg, #667eea15, #764ba215)',
              border: '1px solid #667eea30'
            }}>
            <i className="fas fa-calendar-check me-2" style={{ color: '#667eea' }}></i>
            <span className="fw-semibold" style={{ color: '#667eea' }}>Smart Planning System</span>
          </div>
          <h2 className="display-4 fw-bold mb-3">
            Your <span style={{ 
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Personalized Study Plan</span>
          </h2>
          <p className="lead text-muted mb-0" style={{ maxWidth: '700px', margin: '0 auto' }}>
            Let AI create a customized study roadmap tailored to your goals, available time, 
            and learning pace. Achieve maximum results with intelligent scheduling.
          </p>
        </div>

        {/* Main Content - Image + Features */}
        <div className="row align-items-center g-5 mb-5 pb-4">
          
          {/* Left - Image */}
          <div className="col-lg-6">
            <div className="position-relative">
              <div className="rounded-4 overflow-hidden shadow-lg"
                style={{
                  border: '2px solid #667eea20',
                  background: 'linear-gradient(135deg, #667eea10, #764ba210)'
                }}>
                <img 
                  src="/img/home-v2/study-plan-main.jpg" 
                  alt="AI-Powered Study Plan Dashboard" 
                  className="img-fluid"
                  style={{ width: '100%', height: '450px', objectFit: 'cover' }}
                />
              </div>
              
              {/* Floating Badge */}
              <div className="position-absolute" 
                style={{ 
                  top: '20px', 
                  right: '-20px',
                  animation: 'float 4s ease-in-out infinite'
                }}>
                <div className="rounded-3 p-3 shadow-lg"
                  style={{
                    background: 'rgba(102, 126, 234, 0.95)',
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    minWidth: '180px'
                  }}>
                  <div className="text-center">
                    <i className="fas fa-magic fa-2x mb-2"></i>
                    <div className="fw-bold">AI-Generated</div>
                    <small>In Seconds</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Features Grid */}
          <div className="col-lg-6">
            <div className="row g-4">
              {features.map((feature, index) => (
                <div key={index} className="col-md-6">
                  <div className="h-100">
                    <div className="d-flex align-items-start">
                      <div className="flex-shrink-0">
                        <div className="d-flex align-items-center justify-content-center rounded-circle"
                          style={{
                            width: '50px',
                            height: '50px',
                            background: `${feature.color}15`,
                            border: `2px solid ${feature.color}`,
                            color: feature.color
                          }}>
                          <i className={feature.icon}></i>
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h5 className="fw-bold mb-2">{feature.title}</h5>
                        <p className="text-muted mb-0 small">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* How It Works - Steps */}
        <div className="mt-5 pt-5">
          <h3 className="text-center fw-bold mb-5">How It Works - 5 Simple Steps</h3>
          
          <div className="position-relative">
            {/* Connection Line */}
            <div className="d-none d-md-block position-absolute w-100" 
              style={{ 
                top: '30px', 
                left: '0', 
                height: '4px', 
                background: 'linear-gradient(to right, #667eea, #764ba2)',
                zIndex: 0
              }}></div>
            
            <div className="row g-4 position-relative" style={{ zIndex: 1 }}>
              {planSteps.map((step, index) => (
                <div key={index} className="col-md">
                  <div className="text-center">
                    {/* Step Circle */}
                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3 shadow-lg"
                      style={{
                        width: '70px',
                        height: '70px',
                        background: index % 2 === 0 
                          ? 'linear-gradient(135deg, #667eea, #764ba2)'
                          : 'linear-gradient(135deg, #2d9cdb, #27ae60)',
                        color: 'white',
                        fontSize: '24px',
                        fontWeight: 'bold',
                        border: '4px solid white',
                        marginTop: '-25px'
                      }}>
                      {step.step}
                    </div>
                    
                    {/* Icon */}
                    <div className="mb-2">
                      <i className={`${step.icon} fa-2x`} 
                        style={{ 
                          color: index % 2 === 0 ? '#667eea' : '#2d9cdb' 
                        }}></i>
                    </div>
                    
                    {/* Title & Description */}
                    <h6 className="fw-bold mb-2">{step.title}</h6>
                    <p className="text-muted small mb-0">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-5 pt-5">
          <div className="rounded-4 p-5"
            style={{
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              position: 'relative',
              overflow: 'hidden'
            }}>
            
            {/* Background Circles */}
            <div className="position-absolute rounded-circle" 
              style={{ 
                width: '300px', 
                height: '300px', 
                background: 'rgba(255, 255, 255, 0.1)',
                top: '-100px',
                right: '-50px'
              }}></div>
            <div className="position-absolute rounded-circle" 
              style={{ 
                width: '200px', 
                height: '200px', 
                background: 'rgba(255, 255, 255, 0.08)',
                bottom: '-50px',
                left: '-30px'
              }}></div>

            <div className="position-relative text-white">
              <i className="fas fa-calendar-check fa-3x mb-4" style={{ opacity: 0.9 }}></i>
              <h3 className="fw-bold mb-3">Ready to Plan Your Success?</h3>
              <p className="mb-4 fs-5" style={{ opacity: 0.95, maxWidth: '600px', margin: '0 auto 2rem' }}>
                Get your personalized AI-powered study plan in just 5 minutes. 
                Start preparing smarter, not harder!
              </p>
              
              <div className="d-flex flex-wrap gap-3 justify-content-center">
                <Link to="/study-plan" 
                  className="btn btn-light btn-lg px-5 py-3 rounded-pill shadow-lg">
                  <i className="fas fa-rocket me-2"></i>
                  Create My Study Plan
                </Link>
                <Link to="/signup" 
                  className="btn btn-outline-light btn-lg px-5 py-3 rounded-pill"
                  style={{
                    borderWidth: '2px',
                    backdropFilter: 'blur(10px)',
                    background: 'rgba(255, 255, 255, 0.1)'
                  }}>
                  <i className="fas fa-user-plus me-2"></i>
                  Sign Up Free
                </Link>
              </div>

              {/* Stats */}
              <div className="d-flex flex-wrap gap-4 justify-content-center mt-5 pt-4"
                style={{ borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
                <div>
                  <div className="fw-bold fs-4">5 Min</div>
                  <small style={{ opacity: 0.8 }}>Setup Time</small>
                </div>
                <div style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', paddingLeft: '1.5rem' }}>
                  <div className="fw-bold fs-4">Personalized</div>
                  <small style={{ opacity: 0.8 }}>Adaptive Scheduling</small>
                </div>
                <div style={{ borderLeft: '1px solid rgba(255,255,255,0.3)', paddingLeft: '1.5rem' }}>
                  <div className="fw-bold fs-4">Auto-Sync</div>
                  <small style={{ opacity: 0.8 }}>All Devices</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudyPlanSection;
